import { Injectable, inject } from '@angular/core';
import {
  Auth,
  GoogleAuthProvider,
  OAuthProvider,
  UserCredential,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile
} from '@angular/fire/auth';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { PLATFORM_TOKEN, PlatformProvider } from '@core';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly auth: Auth = inject(Auth);
  private readonly platform: PlatformProvider = inject(PLATFORM_TOKEN);

  async register(email: string, password: string, displayName?: string): Promise<void> {
    if (this.platform.isNativeRun) {
      await FirebaseAuthentication.createUserWithEmailAndPassword({ email, password });
      if (displayName) {
        await FirebaseAuthentication.updateProfile({ displayName });
        // force-refresh the ID token so the new `name` claim lands in the JWT
        // before the first BE call - otherwise UserService.getOrCreateFromFirebaseToken
        // creates the row with empty firstName/lastName (token issued at create-time has no name)
        await FirebaseAuthentication.getIdToken({ forceRefresh: true });
      }
      return;
    }
    const credential: UserCredential = await createUserWithEmailAndPassword(this.auth, email, password);
    if (displayName) {
      await updateProfile(credential.user, { displayName });
      await credential.user.getIdToken(true);
    }
  }

  async login(email: string, password: string): Promise<void> {
    if (this.platform.isNativeRun) {
      await FirebaseAuthentication.signInWithEmailAndPassword({ email, password });
      return;
    }
    await signInWithEmailAndPassword(this.auth, email, password);
  }

  async loginWithGoogle(): Promise<void> {
    if (this.platform.isNativeRun) {
      await FirebaseAuthentication.signInWithGoogle();
      return;
    }
    await signInWithPopup(this.auth, new GoogleAuthProvider());
  }

  async loginWithApple(): Promise<void> {
    if (this.platform.isNativeRun) {
      await FirebaseAuthentication.signInWithApple();
      return;
    }
    await signInWithPopup(this.auth, new OAuthProvider('apple.com'));
  }

  async logout(): Promise<void> {
    if (this.platform.isNativeRun) {
      await FirebaseAuthentication.signOut();
    } else {
      await signOut(this.auth);
    }
  }

  async sendPasswordReset(email: string): Promise<void> {
    if (this.platform.isNativeRun) {
      await FirebaseAuthentication.sendPasswordResetEmail({ email });
      return;
    }
    await sendPasswordResetEmail(this.auth, email);
  }

  async updateDisplayName(displayName: string): Promise<void> {
    if (this.platform.isNativeRun) {
      await FirebaseAuthentication.updateProfile({ displayName });
      // refresh token so the new `name` claim lands before next BE call -
      // BE auto-syncs firstName/lastName/displayName via buildUpdatesFromTokenPayload
      await FirebaseAuthentication.getIdToken({ forceRefresh: true });
      return;
    }
    const user: import('@angular/fire/auth').User | null = this.auth.currentUser;
    if (user === null) throw new Error('No current user');
    await updateProfile(user, { displayName });
    await user.getIdToken(true);
  }

  async getIdToken(forceRefresh: boolean = false): Promise<string | null> {
    if (this.platform.isNativeRun) {
      const result: { token: string } = await FirebaseAuthentication.getIdToken({ forceRefresh });
      return result.token ?? null;
    }
    const user: import('@angular/fire/auth').User | null = this.auth.currentUser;
    return user ? user.getIdToken(forceRefresh) : null;
  }
}
